<template>
    <v-dialog v-model="form.show" width="auto" @afterLeave="close">
        <v-card max-width="100%" width="1200">
            <v-card-title>Upload CSV</v-card-title>
            
            <v-card-text class="pt-4 pb-8">
                <div class="d-flex flex-column ga-6">
                    <div v-if="form.canSubmit">
                        <v-file-input-v1 
                            label="Choose File"
                            v-model="form.file"
                            accept=".csv"
                            show-size
                            :error-messages="form.errors.file"
                            @update:model-value="onFileSelected"
                        >
                            <template #prepend-inner>
                                <mdi-icon>attach_file</mdi-icon>
                            </template>
                        </v-file-input-v1>
                    </div>
                    
                    <div v-if="form.uploadPreview.length" class="d-flex flex-column ga-2">
                        <v-alert color="warning" variant="tonal" border="start" density="compact" class="fs-n2"  v-if="form.canSubmit">
                            Note: Please verify the CSV columns for company data before upload. If not matched, you can look into the sample CSV below for column order.
                        </v-alert>
                        <div class="fs-n2 fw-medium pt-2"  v-if="form.canSubmit">Companies in selected file:</div>
                        <div class="fs-n2 fw-medium pt-2"  v-else>Companies failed to add:</div>
                        <div>
                            <v-row>
                                <v-col v-for="(row, index1) in form.uploadPreview" :key="index1">
                                    <v-sheet color="surface" border class="h-100" >
                                        <v-table class="text-no-wrap" density="compact">
                                            <tbody>
                                                <tr v-if="row.error">
                                                    <th colspan="2" style="width: 1px;" class="text-error">{{ row.error }}</th>
                                                </tr>
                                                <tr v-for="(col, index2) in sampleCsvData.header.fixed" :key="index2">
                                                    <th style="width: 1px;">{{ col }}:</th>
                                                    <td>{{ row.values[index2] ?? '' }}</td>
                                                </tr>
                                                <tr v-for="(col, index2) in sampleCsvData.header.template_variables" :key="index2">
                                                    <th style="width: 1px;">{{ col.label }}:</th>
                                                    <td>{{ row.values[col.serial] ?? '' }}</td>
                                                </tr>
                                            </tbody>
                                        </v-table>
                                    </v-sheet>
                                </v-col>
                            </v-row>
                        </div>
                        
                    </div>
                    <div v-else-if="form.canSubmit">
                        <v-alert color="primary" variant="tonal" border="start" density="compact" class="fs-n2">
                            Note: No Companies found for preview yet
                        </v-alert>
                    </div>
                    <v-divider></v-divider>
                    <div>
                        <v-sheet border color="surface" class="">
                            <div class="d-flex align-center justify-space-between ga-4 pt-4 px-4">
                                <div class="fs-n2 fw-medium">Sample CSV:</div>
                                <div>
                                    <v-btn density="compact" variant="plain" class="text-none px-0" @click="() => { downloadCsv() }" :style="{marginTop: '-2px'}">Download</v-btn>
                                </div>
                            </div>
                            <v-table class="text-no-wrap" density="default">
                                <thead>
                                    <tr>
                                        <th v-for="(value, index) in sampleCsvData.header.fixed" :key="index">{{ value }}</th>
                                        <th v-for="(value, index) in sampleCsvData.header.template_variables" :key="index">{{ value.label }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, index1) in sampleCsvData.items" :key="index1">
                                        <td v-for="(value, index2) in row.fixed" :key="index2">{{ value }}</td>
                                        <td v-for="(value, index2) in row.template_variables" :key="index2">{{ value.value }}</td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </v-sheet>
                    </div>
                    
                </div>
            </v-card-text>
            <v-card-actions-form class="bottom-sticky">
                <div class="d-flex flex-wrap justify-space-between w-100 align-center gx-2 gy-2">
                    <div v-if="form.errors.custom_error" class="text-error">{{ form.errors.custom_error }}</div>
                    <div class="d-flex align-center ga-2 flex-wrap ms-auto" v-if="form.canSubmit">
                        <v-btn
                            text="Cancel"
                            color="btn-secondary"
                            @click="close"
                        ></v-btn>
                        <v-btn
                            :disabled="form.processing"
                            :loading="form.processing"
                            @click="submit"
                            color="primary"
                            text="Upload"
                        ></v-btn>
                    </div>
                    <div class="d-flex align-center ga-2 flex-wrap ms-auto" v-else>
                        <v-btn
                            text="Close"
                            color="btn-secondary"
                            @click="close"
                        ></v-btn>
                    </div>
                </div>
                
            </v-card-actions-form>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
     import { ref } from "vue";
     import { GlobalEventCallback } from '@inertiajs/core';
     import { useForm, usePage, router } from '@inertiajs/vue3';
     import { toast } from 'vue3-toastify';
     import { SharedPage, SharedDataPageProps } from '@/Types/inertia';
     import { User } from '@/Types/Models/user';
     import helpers from '@/Plugins/helpers';
     import { pageTitle, containerGapForForm } from '@/Composables/PageMeta';
     import { Company } from "@/Types/Models/company";
     import { Biller, TemplateVariable } from "@/Types/Models/biller";
     import { useDate } from 'vuetify';
     import { useAuth } from '@/Composables/Auth';
     import papaparse from 'papaparse'

    interface Props {
        biller: Biller,
    }

    const page = usePage<SharedDataPageProps>();
    const props = defineProps<Props>();

    const emit = defineEmits<{
        (e: 'on-success'): void;
    }>();

    type RowType = {
        fixed: string[],
        template_variables: {
            serial: number;
            key: string;
            label: string;
            value?: string;
            map?: string;
        }[],
    }

    const _sampleCsvData = {
        header: ["Company Name","Street address","City","State","Postal code","Country","Advertiser Name","Advertiser Email","Entity Type","Advertiser EIN","Signor(s)/Owner(s) Phone #","IM Username","Guarantor Printed Name","Advertiser Title","Guarantor Title"],
        items: [
            ["Acme Corp","15500 Voss Rd STE 200","Sugar Land","TX","77498","USA","John Doe","john.doe@example.com","LLC","12-3456789","(555) 123-4567","john_doe_acme","John Doe","CEO","Managing Partner"],
            ["Global Media Inc.","500 Park Ave 12th Floor","New York","NY","10022","USA","Jane Smith","jane.smith@globalmedia.com","Corporation","98-7654321","(212) 555-7890","jane_global","Jane Smith","CMO","Chief Financial Officer"],
        ]
    }
    const sampleCsvData = ref<{
        header: RowType,
        items: RowType[]
    }>({
        header: {
            fixed: ["Company Name","Street address","City","State","Postal code","Country","Advertiser Name","Advertiser Email"],
            template_variables: [],
        },
        items: [
            {
                fixed: ["Acme Corp","15500 Voss Rd STE 200","Sugar Land","TX","77498","USA","John Doe","john.doe@example.com"],
                template_variables: [],
            },
            {
                fixed: ["Global Media Inc.","500 Park Ave 12th Floor","New York","NY","10022","USA","Jane Smith","jane.smith@globalmedia.com"],
                template_variables: [],
            }
        ]
    });

    const templateVariables = ref<TemplateVariable>(!Object.keys(props.biller?.additional_data?.template_variables ?? {}).length ? {} : props.biller?.additional_data?.template_variables);
    const templateVariablesSerial = ref(0);
    Object.keys(templateVariables.value).forEach(key => {
        if(templateVariables.value[key] && (!templateVariables.value[key].map || templateVariables.value[key].map == 'user_input')){
            let template_variables_header = {
                key: key,
                serial: templateVariablesSerial.value + sampleCsvData.value.header.fixed.length,
                ...templateVariables.value[key]
            }
            let template_variables_item1 = {
                key: key,
                serial: templateVariablesSerial.value + sampleCsvData.value.header.fixed.length,
                ...templateVariables.value[key]
            }
            let template_variables_item2 = {
                key: key,
                serial: templateVariablesSerial.value + sampleCsvData.value.header.fixed.length,
                ...templateVariables.value[key]
            }
            template_variables_item1.value = template_variables_item1.label + ': value';
            template_variables_item2.value = template_variables_item2.label + ': value';
            sampleCsvData.value.header.template_variables.push(template_variables_header);
            sampleCsvData.value.items[0].template_variables.push(template_variables_item1);
            sampleCsvData.value.items[1].template_variables.push(template_variables_item2);
            
            templateVariablesSerial.value++;
        }
    });


    function toCsv(data: typeof sampleCsvData): string {
        const escape = (val: string) =>
            `"${val?.replace(/"/g, '""') ?? ''}"`; // escape double quotes safely

        const headerRow = [
            ...data.value.header.fixed,
            ...data.value.header.template_variables.map(v => v.label),
        ];

        const itemRows = data.value.items.map(row => [
            ...row.fixed,
            ...row.template_variables.map(v => v.value ?? ''),
        ]);

        const allRows = [headerRow, ...itemRows];

        return allRows.map(row => row.map(escape).join(',')).join('\n');
    }

    function downloadCsv(filename = 'sample.csv') {
        const blob = new Blob([toCsv(sampleCsvData)], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', filename);
        link.click();
    }

    const onFileSelected = async (event: Event) => {
        const file = form.file;
        if (!file){
            form.uploadPreview = [];
            return;
        }

        const text = await file.text();

        const parsed = papaparse.parse<string[]>(text, {
            header: false,
            skipEmptyLines: true,
        });

        const dataRows = parsed.data;

        const previews: {
            error?: string,
            values: string[]
        }[] = [];
        for(let row of dataRows){
            if(row[0] && row[0].trim().toLowerCase() == 'company name'){
                continue;
            }
            previews.push({
            error: '',
            values: row
        });
        }
        form.uploadPreview = previews;
    };

    const form = useForm<{
        show: boolean,
        file: File | null,
        uploadPreview: {
            error?: string,
            values: string[]
        }[],
        canSubmit: boolean,
        custom_error: string,
    }>({
        show: false,
        file: null,
        uploadPreview: [],
        canSubmit: true,
        custom_error: '',
    });

    const close = () => {
        form.reset();
        form.clearErrors();
    }

    const open = () => {
        form.reset();
        form.clearErrors();
        form.show = true;
    }

    const fixedFieldMap = {
        name: 0,
        street_address: 1,
        city: 2,
        state: 3,
        postal_code: 4,
        country: 5,
        placeholder_name: 6,
        placeholder_email: 7,
    } as const;

    const submit = () => {
        let submitData : {
            name: string;
            street_address: string;
            city: string;
            state: string;
            postal_code: string;
            country: string;
            placeholder_name: string;
            placeholder_email: string;
            template_variables: TemplateVariable,
        }[] = [];

        for (let item of form.uploadPreview) {
            const templateVarsMapped: Record<string, { label: string; value: string; map?: string }> = {};

            sampleCsvData.value.header.template_variables.forEach(tv => {
                templateVarsMapped[tv.key] = {
                    label: tv.label,
                    value: item.values[tv.serial] ?? '',
                    ...(tv.map ? { map: tv.map } : {})
                };
            });

            submitData.push({
                name: item.values[fixedFieldMap.name] ?? '',
                street_address: item.values[fixedFieldMap.street_address] ?? '',
                city: item.values[fixedFieldMap.city] ?? '',
                state: item.values[fixedFieldMap.state] ?? '',
                postal_code: item.values[fixedFieldMap.postal_code] ?? '',
                country: item.values[fixedFieldMap.country] ?? '',
                placeholder_name: item.values[fixedFieldMap.placeholder_name] ?? '',
                placeholder_email: item.values[fixedFieldMap.placeholder_email] ?? '',
                template_variables: templateVarsMapped
            });
        }
        // After building submitData
        for (let row of submitData) {
            for (const key in templateVariables.value) {
                if (!row.template_variables[key]) {
                    row.template_variables[key] = templateVariables.value[key];
                }
            }
        }
        console.log(submitData);
        form.transform(() => ({
            companies: submitData
        })).post(route('companies.createByFileStore'), {
            preserveScroll: true,
            preserveState: true,
            onSuccess: (() => {
                const extra: {
                    success: number;
                    failed: number;
                    successMap: Record<number, {
                        success: boolean;
                        msg: string;
                    }>;
                }= page.props.jetstream?.flash?.extra ?? {};
                let status = false;
                let message = '';

                const success = helpers.parseInt(extra.success || 0);
                const failed = helpers.parseInt(extra.failed || 0);

                if (success === 0 && failed === 0) {
                    message = 'No company data found to add.';
                } else if (success > 0 && failed > 0) {
                    message = `${success} compan${success === 1 ? 'y was' : 'ies were'} added, but ${failed} ${failed === 1 ? 'failed to add' : 'failed to add.'}`;
                } else if (success > 0 && failed === 0) {
                    message = `${success} compan${success === 1 ? 'y was' : 'ies were'} added successfully.`;
                    status = true;
                } else if (success === 0 && failed > 0) {
                    message = 'All companies failed to add.';
                }

                if(status){
                    toast.success(message);
                    close();
                }
                else {
                    // Prepare failed preview rows again using successMap
                    const originalPreview = [...form.uploadPreview];

                    const failedPreview = originalPreview.reduce<typeof form.uploadPreview>((acc, row, index) => {
                        const entry = extra.successMap?.[index];
                        if (entry && !entry.success) {
                            acc.push({
                                ...row,
                                error: entry.msg || 'Unknown error occurred while adding company.'
                            });
                        }
                        return acc;
                    }, []);

                    form.uploadPreview = failedPreview;
                    form.canSubmit = false;
                    form.setError('custom_error', message);
                }

                emit('on-success');
            }) as GlobalEventCallback<"success">,
            onFinish: () => {
                form.defaults({
                    show: false,
                    file: null,
                    uploadPreview: [],
                    canSubmit: true,
                    custom_error: '',
                });
            }
        });
    }

    defineExpose({ open });
</script>